import i18n from 'i18next';
import type { TFunction } from 'i18next';

export type FileFormatKind =
  | 'pdf'
  | 'word'
  | 'excel'
  | 'csv'
  | 'powerpoint'
  | 'image'
  | 'audio'
  | 'text'
  | 'archive'
  | 'other';

export const UNKNOWN_FORMAT_LABEL = 'FILE';

const MAX_TILE_LABEL_LENGTH = 4;

const BYTES_PER_UNIT = 1024;

const KIND_BY_EXTENSION = new Map<string, FileFormatKind>([
  ['pdf', 'pdf'],
  ['doc', 'word'],
  ['docx', 'word'],
  ['xls', 'excel'],
  ['xlsx', 'excel'],
  ['csv', 'csv'],
  ['ppt', 'powerpoint'],
  ['pptx', 'powerpoint'],
  ['png', 'image'],
  ['jpg', 'image'],
  ['jpeg', 'image'],
  ['webp', 'image'],
  ['heic', 'image'],
  ['gif', 'image'],
  ['mp3', 'audio'],
  ['m4a', 'audio'],
  ['wav', 'audio'],
  ['txt', 'text'],
  ['md', 'text'],
  ['json', 'text'],
  ['xml', 'text'],
  ['zip', 'archive'],
  ['rar', 'archive'],
  ['7z', 'archive'],
  ['tar', 'archive'],
  ['gz', 'archive'],
  ['tgz', 'archive'],
]);

const TILE_LABEL_BY_EXTENSION = new Map<string, string>([
  ['docx', 'DOC'],
  ['xlsx', 'XLS'],
  ['pptx', 'PPT'],
  ['jpeg', 'JPG'],
]);

function normalizeExtension(extension: string): string {
  return extension.trim().replace(/^\./, '').toLowerCase();
}

export function fileExtension(name: string): string {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return '';
  const extension = trimmed.slice(dot + 1).toLowerCase();
  return /\s/.test(extension) ? '' : extension;
}

export function fileFormatKind(extension: string): FileFormatKind {
  return KIND_BY_EXTENSION.get(normalizeExtension(extension)) ?? 'other';
}

export function fileTileLabel(extension: string): string {
  const normalized = normalizeExtension(extension);
  const label = TILE_LABEL_BY_EXTENSION.get(normalized) ?? normalized.toUpperCase();
  return label.length > 0 && label.length <= MAX_TILE_LABEL_LENGTH ? label : UNKNOWN_FORMAT_LABEL;
}

export function fileMetaLabel(extension: string): string {
  return normalizeExtension(extension).toUpperCase() || UNKNOWN_FORMAT_LABEL;
}

export function isArchiveExtension(extension: string): boolean {
  return fileFormatKind(extension) === 'archive';
}

function formatNumber(value: number, maximumFractionDigits: number, language?: string): string {
  const options = { maximumFractionDigits };
  try {
    return new Intl.NumberFormat(language ?? i18n.language, options).format(value);
  } catch {
    return new Intl.NumberFormat(undefined, options).format(value);
  }
}

export function formatFileSize(bytes: number, t: TFunction<'common'>, language?: string): string {
  const size = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  if (Math.round(size) < BYTES_PER_UNIT) {
    return t('fileCard.units.bytes', '{{value}} B', {
      ns: 'common',
      value: formatNumber(size, 0, language),
    });
  }
  const kilobytes = size / BYTES_PER_UNIT;
  if (Math.round(kilobytes) < BYTES_PER_UNIT) {
    return t('fileCard.units.kilobytes', '{{value}} KB', {
      ns: 'common',
      value: formatNumber(kilobytes, 0, language),
    });
  }
  const megabytes = kilobytes / BYTES_PER_UNIT;
  if (Math.round(megabytes) < BYTES_PER_UNIT) {
    return t('fileCard.units.megabytes', '{{value}} MB', {
      ns: 'common',
      value: formatNumber(megabytes, megabytes < 100 ? 1 : 0, language),
    });
  }
  return t('fileCard.units.gigabytes', '{{value}} GB', {
    ns: 'common',
    value: formatNumber(megabytes / BYTES_PER_UNIT, 1, language),
  });
}

const MIME_TYPE_BY_EXTENSION = new Map<string, string>([
  ['pdf', 'application/pdf'],
  ['doc', 'application/msword'],
  ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ['xls', 'application/vnd.ms-excel'],
  ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ['ppt', 'application/vnd.ms-powerpoint'],
  ['pptx', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  ['csv', 'text/csv'],
  ['txt', 'text/plain'],
  ['md', 'text/markdown'],
  ['json', 'application/json'],
  ['xml', 'application/xml'],
  ['png', 'image/png'],
  ['jpg', 'image/jpeg'],
  ['jpeg', 'image/jpeg'],
  ['webp', 'image/webp'],
  ['gif', 'image/gif'],
  ['heic', 'image/heic'],
  ['mp3', 'audio/mpeg'],
  ['m4a', 'audio/mp4'],
  ['wav', 'audio/wav'],
]);

const BROWSER_PREVIEWABLE_IMAGES: ReadonlySet<string> = new Set([
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
]);

export function mimeTypeForExtension(extension: string): string {
  return MIME_TYPE_BY_EXTENSION.get(normalizeExtension(extension)) ?? 'application/octet-stream';
}

export function isBrowserPreviewableImage(extension: string): boolean {
  return BROWSER_PREVIEWABLE_IMAGES.has(normalizeExtension(extension));
}
